import { ScrollArea } from '@/components/ui/scroll-area'
import { useAi } from '@/context/ai'
import type { ReminderCard as ReminderCardType, UserValidEmailStatusOutput } from '@/shared/api-generated-types'
import ReminderCard from '@/widgets/v2/AI/Cards/Reminder'
import SkeletonCard from '@/widgets/v2/AI/Cards/Skeleton'
import RemindersEmailModal from '@/widgets/v2/AI/Sidebar/Accordion/RelatedReminders/RemindersEmailModal'
import uniqBy from 'lodash/uniqBy'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { gql, useQuery } from 'urql'

const MAX_RECOMMENDATIONS = 20

const FragReminderCard = gql`
  fragment ReminderCard on ReminderCard {
    id
    title
    description
    category
    date
    tags
  }
`

const QUERY_SEARCH_AI_REMINDERS = gql`
  ${FragReminderCard}

  query SearchAIReminders($input: AiSearchInput!) {
    searchAiReminders(input: $input) {
      items {
        ...ReminderCard
      }
    }
  }
`
const QUERY_USER_VALID_EMAIL_STATUS = gql`
  query userValidEmailStatus {
    userValidEmailStatus {
      hasValidEmail
    }
  }
`

interface Props {
  height: number
}

const RelatedReminders = (props: Props): JSX.Element => {
  const {
    state: { query: searchQuery, relatedReminders, subscribedReminders, showRemindersEmailPopup },
    dispatch,
    subscribeReminder,
    unsubscribeReminder,
  } = useAi()

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [currentChangeEvent, setCurrentChangeEvent] = useState<ChangeEvent<HTMLInputElement>>()
  const [currentReminderId, setCurrentReminderId] = useState<string>()

  const [{ data, fetching }] = useQuery<{
    searchAiReminders: { items: ReminderCardType[] }
  }>({
    query: QUERY_SEARCH_AI_REMINDERS,
    pause: searchQuery.trim() === '',
    variables: { input: { query: searchQuery, limit: 5 } },
    requestPolicy: 'cache-first',
  })

  useEffect(() => {
    if (fetching || !data || !data.searchAiReminders?.items.length) return
    data.searchAiReminders.items.map((item) => relatedReminders.unshift(item))
    dispatch({
      type: 'UPDATE',
      payload: { relatedReminders: uniqBy(relatedReminders, 'id').slice(0, MAX_RECOMMENDATIONS) },
    })
  }, [data, fetching])

  const [{ data: userValidEmailStatusData, fetching: fetchingUserValidEmailStatus }] = useQuery<{
    userValidEmailStatus: UserValidEmailStatusOutput
  }>({
    query: QUERY_USER_VALID_EMAIL_STATUS,
    requestPolicy: 'cache-first',
  })

  useEffect(() => {
    if (fetchingUserValidEmailStatus || !userValidEmailStatusData || !userValidEmailStatusData.userValidEmailStatus)
      return

    if (!userValidEmailStatusData.userValidEmailStatus.hasValidEmail) {
      dispatch({
        type: 'UPDATE',
        payload: { showRemindersEmailPopup: true },
      })
    }
  }, [userValidEmailStatusData, fetchingUserValidEmailStatus])

  const handleOnClick = useCallback(
    async (event: ChangeEvent<HTMLInputElement>, reminderId: string): Promise<void> => {
      if (event.currentTarget.checked) {
        if (showRemindersEmailPopup) {
          setCurrentChangeEvent(event)
          setCurrentReminderId(reminderId)
          event.currentTarget.checked = false
          setIsOpen(true)
        } else {
          await subscribeReminder(reminderId)
        }
      } else {
        await unsubscribeReminder(reminderId)
      }
    },
    [showRemindersEmailPopup],
  )

  return (
    <ScrollArea style={{ height: `${props.height * 3 * 0.2}px`, maxHeight: `${props.height * 0.75}px` }}>
      {fetching && <SkeletonCard />}
      {relatedReminders.map((reminder: ReminderCardType) => (
        <ReminderCard
          key={reminder.id}
          {...reminder}
          handleOnClick={(changeEvent: ChangeEvent<HTMLInputElement>) => handleOnClick(changeEvent, reminder.id)}
          isSubscribed={subscribedReminders.indexOf(reminder.id) !== -1}
        />
      ))}
      <RemindersEmailModal
        isOpen={isOpen}
        changeEvent={currentChangeEvent}
        reminderId={currentReminderId}
        close={() => setIsOpen(false)}
      />
    </ScrollArea>
  )
}

export default RelatedReminders
